'use client';

import { useState } from 'react';
import { followerPricingExport } from '@/lib/seo/pricing-exports';
import { Select } from '@mantine/core';
import { NumberField } from '@/components/product/NumberField';
import { PricingResult } from '../../components/calculators/PricingResult';
import styles from '../../components/calculators/tools.module.css';

type ContentType = 'video' | 'story';

const CONTENT_TYPES: { value: ContentType; label: string }[] = [
  { value: 'video', label: 'TikTok Video' },
  { value: 'story', label: 'TikTok Story' },
];

const CONTENT_MULTIPLIERS: Record<ContentType, number> = {
  video: 1.0,
  story: 0.2,
};

const BASE_PER_THOUSAND = 15;
const ENGAGEMENT_DIVISOR = 5;
const LOWER_FACTOR = 0.6;
const UPPER_FACTOR = 1.6;

export function calculateEstimate(
  followers: number,
  engagementRate: number,
  contentType: ContentType,
): { low: number; high: number } | null {
  if (
    !Number.isFinite(followers) ||
    !Number.isFinite(engagementRate) ||
    followers <= 0 ||
    engagementRate <= 0
  ) {
    return null;
  }

  const basePer1k = BASE_PER_THOUSAND;
  const erMultiplier = engagementRate / ENGAGEMENT_DIVISOR;
  const contentMultiplier = CONTENT_MULTIPLIERS[contentType];
  const estimate = (followers / 1000) * basePer1k * erMultiplier * contentMultiplier;
  const low = estimate * LOWER_FACTOR;
  const high = estimate * UPPER_FACTOR;

  if (!Number.isFinite(low) || !Number.isFinite(high)) return null;

  return {
    low: Math.round(low),
    high: Math.round(high),
  };
}

export function PricingCalculator() {
  const [followers, setFollowers] = useState<string>('');
  const [engagementRate, setEngagementRate] = useState<string>('');
  const [contentType, setContentType] = useState<ContentType>('video');

  const followersNum = parseFloat(followers);
  const erNum = parseFloat(engagementRate);
  const result = calculateEstimate(followersNum, erNum, contentType);

  const selectedLabel = CONTENT_TYPES.find((t) => t.value === contentType)?.label ?? 'content';

  return (
    <div className={styles.pricing}>
      <div className={styles.pricingFields}>
        <NumberField
          label="TikTok followers"
          min={0}
          placeholder="e.g. 50000"
          value={followers}
          onChange={(value) => setFollowers(String(value))}
        />
        <NumberField
          label="Follower-based engagement rate"
          suffix="%"
          min={0}
          max={100}
          step={0.1}
          placeholder="e.g. 5.0"
          value={engagementRate}
          onChange={(value) => setEngagementRate(String(value))}
          description="Likes and comments divided by followers. Use the follower-based rate, not the views-based rate in your media kit."
        />
        <Select
          label="Content type"
          data={CONTENT_TYPES}
          value={contentType}
          onChange={(value) => {
            if (value) setContentType(value as ContentType);
          }}
          allowDeselect={false}
        />
      </div>

      <PricingResult
        result={result}
        format={selectedLabel}
        exportText={followerPricingExport({
          platform: 'tiktok',
          followers: followersNum,
          engagementRate: erNum,
          format: selectedLabel,
          result,
          model: {
            basePerThousand: BASE_PER_THOUSAND,
            engagementDivisor: ENGAGEMENT_DIVISOR,
            formatMultiplier: CONTENT_MULTIPLIERS[contentType],
            lowerFactor: LOWER_FACTOR,
            upperFactor: UPPER_FACTOR,
          },
        })}
        exportTool="tiktok-influencer-pricing-calculator"
      />
    </div>
  );
}
